// MUI + React + a literals-only registry: the prerendered splash pages import this, so nothing here may reach the store, window at module scope, or ui/Components/Dialog (its AppBar imports the store).
import {useState, type ReactNode} from 'react'
import type {SxProps, Theme} from '@mui/material/styles'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import ButtonBase from '@mui/material/ButtonBase'
import Dialog from '@mui/material/Dialog'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded'
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutlineOutlined'
import CloseIcon from '@mui/icons-material/Close'
import {EXPLAINER_VIDEOS, videoLength, type VideoKey} from '../../../../shared/utils/explainerVideos'

type Props = {name: VideoKey; priority?: boolean; fallback?: ReactNode; autoPlay?: boolean; sx?: SxProps<Theme>}

/** A YouTube facade: a self-hosted poster until pressed, and only then the youtube-nocookie iframe, so a page view sends nothing to Google. Renders `fallback` (default nothing) while the video has no id; `sx` styles a wrapper that exists only when something renders. */
export function ExplainerVideo({name, priority = false, fallback = null, autoPlay = false, sx}: Props) {
  const v = EXPLAINER_VIDEOS[name]
  const [playing, setPlaying] = useState(autoPlay)
  const len = videoLength(v.seconds)
  const body = !v.id ? fallback : <Box sx={{position: 'relative', aspectRatio: '16 / 9', width: '100%', borderRadius: 3, overflow: 'hidden', bgcolor: '#000'}}>
    {playing
      ? <Box component="iframe" src={`https://www.youtube-nocookie.com/embed/${v.id}?autoplay=1&rel=0`} title={v.title} allow="autoplay; encrypted-media; picture-in-picture; fullscreen" allowFullScreen sx={{position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0}} />
      : <ButtonBase onClick={() => setPlaying(true)} aria-label={`${v.title}, ${len}. Plays from YouTube`} sx={{position: 'absolute', inset: 0, display: 'block', color: '#fff'}}>
        <Box component="img" src={v.poster} alt="" fetchPriority={priority ? 'high' : undefined} loading={priority ? undefined : 'lazy'} sx={{position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover'}} />
        <Box sx={{position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.7), rgba(0,0,0,0) 45%)'}} />
        <Box sx={{position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: 72, height: 72, borderRadius: '50%', display: 'grid', placeItems: 'center', bgcolor: 'rgba(0,0,0,0.6)'}}><PlayArrowRoundedIcon sx={{fontSize: 48}} /></Box>
        <Box sx={{position: 'absolute', left: 12, right: 12, bottom: 10, display: 'flex', alignItems: 'center', gap: 1}}>
          <Box component="span" sx={{px: 1, py: 0.25, borderRadius: 1, bgcolor: 'rgba(0,0,0,0.75)', fontSize: 13, fontWeight: 700, fontVariantNumeric: 'tabular-nums'}}>{len}</Box>
          <Typography component="span" sx={{fontSize: 13, fontWeight: 600}}>Plays from YouTube</Typography>
        </Box>
      </ButtonBase>}
  </Box>
  return body && sx ? <Box sx={sx}>{body}</Box> : <>{body}</>
}

/** "How it works (m:ss)": opens the video in a dialog, already playing (the press is the consent). Nothing while the video has no id. */
export function HowItWorksButton({name}: {name: VideoKey}) {
  const [open, setOpen] = useState(false)
  const v = EXPLAINER_VIDEOS[name]
  if (!v.id) return null
  return <>
    <Button size="small" startIcon={<PlayCircleOutlineIcon />} onClick={() => setOpen(true)}>How it works ({videoLength(v.seconds)})</Button>
    <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth slotProps={{paper: {'aria-label': v.title}}}>
      <Box sx={{display: 'flex', justifyContent: 'flex-end'}}><IconButton onClick={() => setOpen(false)} aria-label="close"><CloseIcon /></IconButton></Box>
      <Box sx={{px: 2, pb: 2}}>{open && <ExplainerVideo name={name} autoPlay />}</Box>
    </Dialog>
  </>
}
